/**
 * Assemblage et lecture d'une adresse postale.
 *
 * Une adresse se compose de morceaux qui manquent souvent : un client noté à
 * la hâte n'a qu'un nom. Assemblés sans précaution, ces vides donnaient
 * « , » — une épingle sur la carte qui ne désigne rien, et un bon de
 * livraison qui annonce une destination qu'il n'a pas.
 */

/**
 * Assemble les morceaux d'une adresse en ne gardant que ceux qui existent.
 *
 * @param {...(string|null|undefined)} morceaux rue, NPA, localité…
 * @returns {string} l'adresse, ou une chaîne vide si rien n'est connu
 */
export function adresseAssemblee(...morceaux) {
  return morceaux
    .map((m) => String(m ?? '').trim())
    .filter(Boolean)
    .join(', ');
}

/**
 * Rend une adresse si elle dit quelque chose, rien sinon.
 *
 * <p>Les adresses déjà enregistrées portent la trace de l'ancien assemblage :
 * « , », « , 1000 Lausanne ». Les nettoyer à la lecture évite de les réécrire
 * toutes.
 *
 * @param {string|null|undefined} adresse adresse telle qu'enregistrée
 * @returns {string} l'adresse nettoyée, vide si elle ne désigne rien
 */
export function adresseLisible(adresse) {
  return adresseAssemblee(...String(adresse ?? '').split(','));
}

/** NPA de 4 ou 5 chiffres suivi de la localité, en fin de champ. */
const NPA_FINAL = /(?:^|[,\s])(\d{4,5})\s+([^,\d][^,]*)$/;

const espacesSimples = (texte) =>
  String(texte ?? '')
    .replace(/[\u00A0\u202F]/g, ' ')
    .replace(/ {2,}/g, ' ');

const sansVirguleFinale = (texte) =>
  espacesSimples(texte)
    .trim()
    .replace(/[,\s]+$/, '');

/** Code pays devant le NPA (« CH 8000 Zürich ») : ce n'est pas une rue. */
const CODE_PAYS = /^[A-Za-z]{1,3}$/;

/** Longueur de la rue enregistrée (colonne `adr`). */
const LONGUEUR_RUE = 200;

/**
 * Rue d'un côté, « NPA localité » de l'autre, pour l'adresse d'un client.
 *
 * <p>Une adresse reprise des contacts arrivait parfois en double :
 * « Rte de la Parraille 7, » en rue et « Rte de la Parraille 7, 1287 Laconnex »
 * en NPA, et l'offre l'imprimait telle quelle. La rue répétée devant le NPA
 * est retirée — ou passe dans la rue quand elle n'y était pas —, et la virgule
 * finale de la rue tombe. Une adresse propre, ou sans NPA reconnaissable
 * (étranger, ou précédé d'un code pays), reste telle quelle. Même règle que le serveur
 * (AdressePostale.java), qui l'applique aussi au QR-facture.
 *
 * @param {string} [adr] rue et numéro
 * @param {string} [npa] NPA et localité
 * @returns {{adr: string, npa: string}} l'adresse remise en ordre
 */
export function adresseNette(adr, npa) {
  const rue = sansVirguleFinale(adr);
  const n = espacesSimples(npa).trim();
  const m = n.match(NPA_FINAL);
  const debutNpa = m ? m.index + m[0].indexOf(m[1]) : 0;
  if (!m || debutNpa === 0) return { adr: rue, npa: n };
  const devant = sansVirguleFinale(n.slice(0, debutNpa));
  if (CODE_PAYS.test(devant)) return { adr: rue, npa: n };
  // Chaque morceau écrit devant le NPA passe dans la rue, sauf s'il y est déjà
  // en entier : « 7 » ne se perd pas derrière « Parraille 17 ».
  const deLaRue = rue
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  let fusion = rue;
  for (const morceau of devant.split(',')) {
    const propre = morceau.trim();
    if (propre && !deLaRue.includes(propre.toLowerCase())) {
      fusion = fusion ? fusion + ', ' + propre : propre;
    }
  }
  if (fusion.length > LONGUEUR_RUE) return { adr: rue, npa: n };
  return { adr: fusion, npa: m[1] + ' ' + m[2].trim() };
}
